<template>
  <div class="bi-control-panel-table-container pr-5">
    <v-card>
      <InfiniteScroll
        class="bi-control-panel-table bi-control-panel-table--approval"
        item-key="id"
        :headers="headers"
        :items="biApprovalData"
        :loading="tableLoading"
        :has-more="tableHasMore"
        :auto-load-on-mount="false"
        mobile-breakpoint="0"
        fixed-header
        hide-default-footer
        disable-pagination
        @load-more="loadMore"
      >
        <template #item.approvalStatus="{ item }">
          <div class="d-flex align-center bi-control-panel-status-cell">
            <v-switch
              v-if="canEdit"
              :input-value="item.approvalStatus"
              color="primary"
              hide-details
              inset
              dense
              :disabled="isStatusUpdating(item.id)"
              :loading="isStatusUpdating(item.id)"
              class="status-switch mr-2 mt-0 pt-0"
              @change="updateStatus(item, $event)"
            />
            <span>
              {{ item.approvalStatus ? $t('biControlPanel.table.active') : $t('biControlPanel.table.inactive') }}
            </span>
          </div>
        </template>

        <template #item.competency="{ item }">
          {{ formatCompetency(item.competency) }}
        </template>
      </InfiniteScroll>
    </v-card>
    <SnackbarCustomize ref="SnackbarCustomize" />
  </div>
</template>

<script>
import moment from 'moment';
import InfiniteScroll from '@/components/Default/InfiniteScroll.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import BIControlPanelModule from '@/store/modules/bi-control-panel/bi-control-panel-module';

export default {
  name: 'BIApprovalTable',
  components: { InfiniteScroll, SnackbarCustomize },
  props: {
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { updatingStatusIds: [] };
  },
  computed: {
    tableState() {
      return BIControlPanelModule.approvalTable;
    },
    biApprovalData() {
      return this.tableState.rows;
    },
    tableLoading() {
      return this.tableState.loading;
    },
    tableHasMore() {
      return this.tableState.pagination.hasMore;
    },
    headers() {
      return [
        {
          text: this.translate('biControlPanel.filters.financialGroup'),
          value: 'financialGroup',
          class: 'col-financial-group',
          cellClass: 'col-financial-group',
        },
        {
          text: this.translate('biControlPanel.filters.contract'),
          value: 'contract',
          class: 'col-contract',
          cellClass: 'col-contract',
        },
        {
          text: this.translate('biControlPanel.filters.carrier'),
          value: 'carrier',
          class: 'col-carrier',
          cellClass: 'col-carrier',
        },
        {
          text: this.translate('biControlPanel.filters.competency'),
          value: 'competency',
          class: 'col-competency',
          cellClass: 'col-competency',
        },
        {
          text: this.translate('biControlPanel.table.status'),
          value: 'approvalStatus',
          class: 'col-status',
          cellClass: 'col-status',
        },
        {
          text: this.translate('biControlPanel.table.lastUpdateDate'),
          value: 'updateDate',
          class: 'col-update-date',
          cellClass: 'col-update-date',
        },
        {
          text: this.translate('biControlPanel.table.updateUser'),
          value: 'updateUser',
          class: 'col-update-user',
          cellClass: 'col-update-user',
        },
      ];
    },
  },
  methods: {
    formatCompetency(value) {
      if (!value) {
        return '';
      }

      const competency = moment(value, 'YYYY-MM', true);

      if (!competency.isValid()) {
        return value;
      }

      return competency.format('MM/YYYY');
    },
    isStatusUpdating(id) {
      return this.updatingStatusIds.includes(id);
    },
    setStatusUpdating(id, loading) {
      if (loading) {
        if (!this.isStatusUpdating(id)) {
          this.updatingStatusIds = [...this.updatingStatusIds, id];
        }
        return;
      }

      this.updatingStatusIds = this.updatingStatusIds.filter((itemId) => itemId !== id);
    },
    updateStatus(item, value) {
      const nextStatus = Boolean(value);

      if (item.approvalStatus === nextStatus || this.isStatusUpdating(item.id)) {
        return;
      }

      this.setStatusUpdating(item.id, true);
      this.runStoreAction(
        BIControlPanelModule.updateApprovalStatus({
          id: item.id,
          approvalStatus: nextStatus,
        }),
        this.translate('biControlPanel.messages.statusUpdateError'),
      ).finally(() => {
        this.setStatusUpdating(item.id, false);
      });
    },
    async loadMore() {
      await this.runStoreAction(
        BIControlPanelModule.loadMoreApprovalTable(),
        this.translate('biControlPanel.messages.tableDataSearchError'),
      );
    },
    translate(key) {
      return this.$t(key).toString();
    },
    showError(message) {
      this.$refs.SnackbarCustomize.open('error', message);
    },
    async runStoreAction(actionPromise, fallbackMessage) {
      const result = await actionPromise;

      if (result && result.success === false) {
        this.showError(result.errorMessage || fallbackMessage);
      }

      return result;
    },
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/biControlPanel/table.scss";
</style>
